/**
 * Avatar System Constants
 * Provides preset avatars and default avatar functionality
 */

import { getPremiumAvatar } from '../types/premiumAvatars';
import type { Rarity } from '../types/rarity';

export interface PresetAvatar {
  id: string;
  emoji: string;
  name: string;
  category: 'animals' | 'people' | 'objects' | 'symbols';
}

export const PRESET_AVATARS: PresetAvatar[] = [
  // Animals
  { id: 'cat', emoji: '🐱', name: 'Cat', category: 'animals' },
  { id: 'dog', emoji: '🐶', name: 'Dog', category: 'animals' },
  { id: 'fox', emoji: '🦊', name: 'Fox', category: 'animals' },
  { id: 'bear', emoji: '🐻', name: 'Bear', category: 'animals' },
  { id: 'panda', emoji: '🐼', name: 'Panda', category: 'animals' },
  { id: 'lion', emoji: '🦁', name: 'Lion', category: 'animals' },
  { id: 'tiger', emoji: '🐯', name: 'Tiger', category: 'animals' },
  { id: 'monkey', emoji: '🐵', name: 'Monkey', category: 'animals' },
  { id: 'penguin', emoji: '🐧', name: 'Penguin', category: 'animals' },
  { id: 'owl', emoji: '🦉', name: 'Owl', category: 'animals' },
  { id: 'eagle', emoji: '🦅', name: 'Eagle', category: 'animals' },
  { id: 'unicorn', emoji: '🦄', name: 'Unicorn', category: 'animals' },
  { id: 'wolf', emoji: '🐺', name: 'Wolf', category: 'animals' },
  { id: 'dragon', emoji: '🐉', name: 'Dragon', category: 'animals' },
  { id: 'turtle', emoji: '🐢', name: 'Turtle', category: 'animals' },
  { id: 'butterfly', emoji: '🦋', name: 'Butterfly', category: 'animals' },
  { id: 'octopus', emoji: '🐙', name: 'Octopus', category: 'animals' },
  { id: 'dino', emoji: '🦖', name: 'Dino', category: 'animals' },
  { id: 'whale', emoji: '🐳', name: 'Whale', category: 'animals' },
  { id: 'hedgehog', emoji: '🦔', name: 'Hedgehog', category: 'animals' },
  { id: 'bee', emoji: '🐝', name: 'Bee', category: 'animals' },
  { id: 'flamingo', emoji: '🦩', name: 'Flamingo', category: 'animals' },
  { id: 'snake', emoji: '🐍', name: 'Snake', category: 'animals' },
  { id: 'rabbit', emoji: '🐰', name: 'Rabbit', category: 'animals' },

  // People
  { id: 'ninja', emoji: '🥷', name: 'Ninja', category: 'people' },
  { id: 'astronaut', emoji: '👨‍🚀', name: 'Astronaut', category: 'people' },
  { id: 'robot', emoji: '🤖', name: 'Robot', category: 'people' },
  { id: 'ghost', emoji: '👻', name: 'Ghost', category: 'people' },
  { id: 'alien', emoji: '👽', name: 'Alien', category: 'people' },
  { id: 'wizard', emoji: '🧙', name: 'Wizard', category: 'people' },
  { id: 'superhero', emoji: '🦸', name: 'Superhero', category: 'people' },
  { id: 'detective', emoji: '🕵️', name: 'Detective', category: 'people' },
  { id: 'vampire', emoji: '🧛', name: 'Vampire', category: 'people' },
  { id: 'elf', emoji: '🧝', name: 'Elf', category: 'people' },
  { id: 'scientist', emoji: '🧑‍🔬', name: 'Scientist', category: 'people' },
  { id: 'rockstar', emoji: '🧑‍🎤', name: 'Rockstar', category: 'people' },
  { id: 'zombie', emoji: '🧟', name: 'Zombie', category: 'people' },
  { id: 'fairy', emoji: '🧚', name: 'Fairy', category: 'people' },
  { id: 'genie', emoji: '🧞', name: 'Genie', category: 'people' },
  { id: 'mage', emoji: '🪄', name: 'Mage', category: 'people' },

  // Objects
  { id: 'rocket', emoji: '🚀', name: 'Rocket', category: 'objects' },
  { id: 'fire', emoji: '🔥', name: 'Fire', category: 'objects' },
  { id: 'gem', emoji: '💎', name: 'Gem', category: 'objects' },
  { id: 'crown', emoji: '👑', name: 'Crown', category: 'objects' },
  { id: 'trophy', emoji: '🏆', name: 'Trophy', category: 'objects' },
  { id: 'laptop', emoji: '💻', name: 'Laptop', category: 'objects' },
  { id: 'brain', emoji: '🧠', name: 'Brain', category: 'objects' },
  { id: 'lightning', emoji: '⚡', name: 'Lightning', category: 'objects' },
  { id: 'controller', emoji: '🎮', name: 'Controller', category: 'objects' },
  { id: 'books', emoji: '📚', name: 'Books', category: 'objects' },
  { id: 'dart', emoji: '🎯', name: 'Dart', category: 'objects' },
  { id: 'palette', emoji: '🎨', name: 'Palette', category: 'objects' },
  { id: 'ufo', emoji: '🛸', name: 'UFO', category: 'objects' },
  { id: 'dice', emoji: '🎲', name: 'Dice', category: 'objects' },
  { id: 'guitar', emoji: '🎸', name: 'Guitar', category: 'objects' },
  { id: 'medal', emoji: '🥇', name: 'Medal', category: 'objects' },
  { id: 'compass', emoji: '🧭', name: 'Compass', category: 'objects' },

  // Symbols
  { id: 'star', emoji: '⭐', name: 'Star', category: 'symbols' },
  { id: 'sparkles', emoji: '✨', name: 'Sparkles', category: 'symbols' },
  { id: 'sun', emoji: '☀️', name: 'Sun', category: 'symbols' },
  { id: 'moon', emoji: '🌙', name: 'Moon', category: 'symbols' },
  { id: 'planet', emoji: '🪐', name: 'Planet', category: 'symbols' },
  { id: 'comet', emoji: '☄️', name: 'Comet', category: 'symbols' },
  { id: 'glow-star', emoji: '🌟', name: 'Glow Star', category: 'symbols' },
  { id: 'shooting-star', emoji: '🌠', name: 'Shooting Star', category: 'symbols' },
  { id: 'volcano', emoji: '🌋', name: 'Volcano', category: 'symbols' },
  { id: 'crystal-ball', emoji: '🔮', name: 'Crystal Ball', category: 'symbols' },
  { id: 'snowflake', emoji: '❄️', name: 'Snowflake', category: 'symbols' },
  { id: 'rainbow', emoji: '🌈', name: 'Rainbow', category: 'symbols' },
  { id: 'fireworks', emoji: '🎆', name: 'Fireworks', category: 'symbols' },
];

export const DEFAULT_AVATAR = '👤'; // Default user icon

export interface AvatarGradient {
  id: string;
  name: string;
  gradient: string;
}

// Each gradient travels across distinct hues (often 3 stops) so it reads as a
// true gradient rather than a near-flat colour. IDs are unchanged so existing
// saved profiles keep resolving.
export const AVATAR_GRADIENTS: AvatarGradient[] = [
  { id: 'purple', name: 'Purple Dream', gradient: 'linear-gradient(135deg, #6a11cb 0%, #c471ed 50%, #2575fc 100%)' },
  { id: 'orange', name: 'Sunset', gradient: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)' },
  { id: 'blue', name: 'Ocean', gradient: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)' },
  { id: 'green', name: 'Forest', gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
  { id: 'pink', name: 'Candy', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'yellow', name: 'Sunshine', gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' },
  { id: 'red', name: 'Fire', gradient: 'linear-gradient(135deg, #f83600 0%, #f9d423 100%)' },
  { id: 'teal', name: 'Mint', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
  { id: 'indigo', name: 'Twilight', gradient: 'linear-gradient(135deg, #4568dc 0%, #b06ab3 100%)' },
  { id: 'cyan', name: 'Ice', gradient: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)' },
  { id: 'rose', name: 'Rose', gradient: 'linear-gradient(135deg, #ee9ca7 0%, #e94057 50%, #8a2387 100%)' },
  { id: 'amber', name: 'Amber', gradient: 'linear-gradient(135deg, #fceabb 0%, #f8b500 100%)' },
  { id: 'dark', name: 'Dark', gradient: 'linear-gradient(135deg, #434343 0%, #000000 100%)' },
  { id: 'slate', name: 'Slate', gradient: 'linear-gradient(135deg, #64748b 0%, #1e293b 100%)' },
];

export const SOLID_COLORS = [
  { id: 'gray', name: 'Gray', color: '#6b7280' },
  { id: 'red', name: 'Red', color: '#ef4444' },
  { id: 'orange', name: 'Orange', color: '#f97316' },
  { id: 'yellow', name: 'Yellow', color: '#f59e0b' },
  { id: 'green', name: 'Green', color: '#10b981' },
  { id: 'blue', name: 'Blue', color: '#3b82f6' },
  { id: 'purple', name: 'Purple', color: '#8b5cf6' },
  { id: 'pink', name: 'Pink', color: '#ec4899' },
  { id: 'black', name: 'Black', color: '#1f2937' },
];

// ── Earned-via-box gating ────────────────────────────────────────────────
// Most "fun" preset avatars and colourful gradients are earned by opening
// surprise boxes; a small starter set stays free so new users always have
// something. Solid colours stay free. See CHALLENGES_SYSTEM.md / boxes.

const FREE_BASIC_AVATAR_IDS = new Set(['cat', 'dog', 'fox', 'bear', 'panda', 'rabbit']);

/** People category + a few basic animals stay free; the rest are box-earned. */
export function isPresetAvatarFree(a: PresetAvatar): boolean {
  return a.category === 'people' || FREE_BASIC_AVATAR_IDS.has(a.id);
}

const PRESET_AVATAR_RARITY: Record<string, Rarity> = {
  dragon: 'legendary', unicorn: 'epic', dino: 'epic',
  wolf: 'rare', owl: 'rare', eagle: 'rare', octopus: 'rare', whale: 'rare',
  flamingo: 'rare', hedgehog: 'rare',
  gem: 'epic', crown: 'epic', ufo: 'epic',
  trophy: 'rare', brain: 'rare', lightning: 'rare', medal: 'rare', compass: 'rare', guitar: 'rare',
};

export function presetAvatarRarity(a: PresetAvatar): Rarity {
  return PRESET_AVATAR_RARITY[a.id] ?? (a.category === 'symbols' ? 'rare' : 'common');
}

export function getPresetAvatar(id: string | undefined): PresetAvatar | undefined {
  if (!id) return undefined;
  return PRESET_AVATARS.find(a => a.id === id);
}

/** Earned (non-free) preset avatars — the box drop pool source. */
export function getEarnedPresetAvatars(): PresetAvatar[] {
  return PRESET_AVATARS.filter(a => !isPresetAvatarFree(a));
}

const FREE_GRADIENT_IDS = new Set(['purple', 'slate', 'dark']);

export function isGradientFree(id: string): boolean {
  return FREE_GRADIENT_IDS.has(id);
}

const GRADIENT_RARITY: Record<string, Rarity> = {
  rose: 'epic', amber: 'epic',
  indigo: 'rare', cyan: 'rare', teal: 'rare', pink: 'rare', red: 'rare',
};

export function gradientRarity(id: string): Rarity {
  return GRADIENT_RARITY[id] ?? 'common';
}

export function getGradient(id: string | undefined): AvatarGradient | undefined {
  if (!id) return undefined;
  return AVATAR_GRADIENTS.find(g => g.id === id);
}

/** Earned (non-free) gradients — the box drop pool source. */
export function getEarnedGradients(): AvatarGradient[] {
  return AVATAR_GRADIENTS.filter(g => !isGradientFree(g.id));
}

/**
 * Get the background style for avatar
 */
export function getAvatarBackground(profile: {
  avatarBackgroundType?: 'gradient' | 'solid';
  avatarBackgroundValue?: string;
}): string {
  if (profile.avatarBackgroundType === 'gradient' && profile.avatarBackgroundValue) {
    const gradient = AVATAR_GRADIENTS.find(g => g.id === profile.avatarBackgroundValue);
    return gradient?.gradient || AVATAR_GRADIENTS[0].gradient;
  }
  
  if (profile.avatarBackgroundType === 'solid' && profile.avatarBackgroundValue) {
    const solid = SOLID_COLORS.find(c => c.id === profile.avatarBackgroundValue);
    return solid?.color || SOLID_COLORS[0].color;
  }
  
  // Default: purple gradient
  return AVATAR_GRADIENTS[0].gradient;
}

/**
 * Get the display avatar for a user profile
 * NOTE: SSO photos are ignored - only custom avatars (preset emoji or uploaded images)
 */
export function getDisplayAvatar(profile: {
  customAvatarType?: 'sso' | 'preset' | 'upload' | 'default' | 'premium';
  customAvatarValue?: string;
  photoURL?: string;
  displayName?: string;
}): { type: 'emoji' | 'image' | 'initial'; value: string } {
  // Priority: premium > upload > preset > initial (NO SSO photos)

  if (profile.customAvatarType === 'premium' && profile.customAvatarValue) {
    const premium = getPremiumAvatar(profile.customAvatarValue);
    if (premium) return { type: 'image', value: premium.assetPath };
  }

  if (profile.customAvatarType === 'upload' && profile.customAvatarValue) {
    return { type: 'image', value: profile.customAvatarValue };
  }

  if (profile.customAvatarType === 'preset' && profile.customAvatarValue) {
    const preset = PRESET_AVATARS.find(a => a.id === profile.customAvatarValue);
    return { type: 'emoji', value: preset?.emoji || DEFAULT_AVATAR };
  }
  
  // SSO photos are intentionally skipped - they cause broken images
  // Users must choose a preset avatar or upload their own
  
  // Fallback: first letter of name
  if (profile.displayName) {
    return { type: 'initial', value: profile.displayName.charAt(0).toUpperCase() };
  }
  
  return { type: 'emoji', value: DEFAULT_AVATAR };
}

